// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toApiError } from "@/api/api-error";
import { api } from "@/api/client";
import { useSubmitPendingActionMutation } from "@/api/hooks/use-pending-actions";
import { useAuth } from "@/lib/auth";
import { useFeatureFlags } from "@/lib/feature-flags";
import type { ActionPayload, CreateChainPayload } from "@/lib/pending-actions/types";

type SubmitOutcome = "executed" | "scheduled" | "submitted-for-approval";

interface SubmitActionOptions {
  scheduledFor?: string;
  message?: string;
}

// ── Direct executors (admin immediate path) ─────────────────────────────────

async function executeCreateChain(payload: CreateChainPayload, queryClient: ReturnType<typeof useQueryClient>) {
  const { copyFrom, type: _, ...body } = payload;
  const res = await api.POST("/v1/chains", {
    params: { query: { copyFrom: copyFrom || undefined } },
    body,
  });
  if (res.error) throw toApiError(res.error, res.response, "Failed to create chain");
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["chains"] }),
    // The shared backoffice context feeds chain pickers/filters across every view — refresh it
    // so the freshly created chain shows up without a reload.
    queryClient.invalidateQueries({ queryKey: ["backoffice", "context"] }),
  ]);
}

async function executeDirectly(payload: ActionPayload, queryClient: ReturnType<typeof useQueryClient>) {
  switch (payload.type) {
    case "create-chain":
      return executeCreateChain(payload, queryClient);
  }
}

// ── Hook ────────────────────────────────────────────────────────────────────

export function useSubmitAction() {
  const { user } = useAuth();
  const { approvalFlow } = useFeatureFlags();
  const queryClient = useQueryClient();
  const submitMutation = useSubmitPendingActionMutation();
  // Wrap the immediate (admin / approval-off) path in a mutation so its pending state is tracked
  const directMutation = useMutation({
    mutationFn: (payload: ActionPayload) => executeDirectly(payload, queryClient),
  });
  const isAdmin = user?.groups.includes("admin") ?? false;
  // When the approval flow is disabled, every user submits directly (admin-equivalent path).
  const directExecute = isAdmin || !approvalFlow;

  async function submitAction(
    payload: ActionPayload,
    options?: SubmitActionOptions,
  ): Promise<{ outcome: SubmitOutcome }> {
    // Direct executor with no schedule: run immediately
    if (directExecute && !options?.scheduledFor) {
      await directMutation.mutateAsync(payload);
      return { outcome: "executed" };
    }

    // Direct executor with schedule: submit as pending action (backend self-approves and schedules)
    if (directExecute && options?.scheduledFor) {
      await submitMutation.mutateAsync({
        actionType: payload.type,
        payload,
        scheduledFor: options.scheduledFor,
        message: options.message,
      });
      return { outcome: "scheduled" };
    }

    // Non-admin with approval flow on: submit for approval
    await submitMutation.mutateAsync({
      actionType: payload.type,
      payload,
      scheduledFor: options?.scheduledFor,
    });
    return { outcome: "submitted-for-approval" };
  }

  return { submitAction, isAdmin, isPending: submitMutation.isPending || directMutation.isPending };
}
